/**
 * IndexedDB utility for managing application data storage.
 * Provides a centralized database configuration and access methods.
 *
 * This module provides low-level database operations. Feature-specific stores
 * should register their upgrade handlers using registerStoreUpgrade().
 *
 * IMPORTANT - Class Instance Reconstruction Pattern:
 * IndexedDB serializes all stored objects as plain JSON, which means class instances
 * lose their prototype methods when stored and retrieved. When you store a class instance
 * like `new CampusData(...)`, it gets saved as a plain object `{}`, and when you retrieve
 * it, you get back that plain object WITHOUT any class methods.
 *
 * To restore class functionality, you MUST reconstruct class instances in your getter
 * functions after retrieval from IndexedDB. This is typically done using static factory
 * methods like `fromRecord()` or `fromJson()`.
 *
 * Example WITHOUT reconstruction (BROKEN):
 *   const data = await getRecord('store', 'key')
 *   data.getBuildingById('123')  // ERROR: getBuildingById is not a function
 *
 * Example WITH reconstruction (CORRECT):
 *   const record = await getRecord('store', 'key')
 *   const data = CampusData.fromRecord(record)  // Restores class methods
 *   data.getBuildingById('123')  // ✓ Works correctly
 *
 * See libs/js/shared/pages/map/workers/store.js for real-world examples of this pattern.
 */

// Database configuration
export const DB_NAME = "admin-app-db"
export const DB_VERSION = 5

// Store upgrade handlers - feature modules can register their store creation logic
const storeUpgradeHandlers = []

/**
 * Registers a store upgrade handler to be called during database initialization.
 * Feature modules should use this to define their own stores without polluting
 * the global db.js file.
 *
 * @param {function(IDBDatabase): void} handler - Function that creates/upgrades stores
 *
 * @example
 * registerStoreUpgrade((db) => {
 *   if (!db.objectStoreNames.contains('my_store')) {
 *     db.createObjectStore('my_store', { keyPath: 'id' })
 *   }
 * })
 */
export const registerStoreUpgrade = handler => {
  storeUpgradeHandlers.push(handler)
}

/**
 * Opens or creates the IndexedDB database with required object stores.
 * Automatically handles database upgrades and calls all registered store handlers.
 *
 * @returns {Promise<IDBDatabase>} A promise that resolves to the database instance
 */
export const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)

    request.onupgradeneeded = event => {
      const db = event.target.result

      // Call all registered store upgrade handlers with db and event
      for (const handler of storeUpgradeHandlers) {
        handler(db, event)
      }
    }
  })
}

/**
 * Retrieves a record from a specified store by key.
 *
 * @param {string} storeName - The name of the object store
 * @param {string|Array<string>} key - The key or compound key to retrieve
 * @returns {Promise<any>} A promise that resolves to the record or null if not found
 */
export const getRecord = async (storeName, key) => {
  const db = await openDB()
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], "readonly")
      const store = transaction.objectStore(storeName)
      const request = store.get(key)

      request.onsuccess = () => {
        db.close()
        resolve(request.result || null)
      }
      request.onerror = () => {
        db.close()
        reject(request.error)
      }
    })
  } catch (error) {
    db.close()
    throw error
  }
}

/**
 * Stores a record in a specified store.
 *
 * @param {string} storeName - The name of the object store
 * @param {any} record - The record to store (must include keyPath fields)
 * @returns {Promise<void>} A promise that resolves when the record is stored
 */
export const putRecord = async (storeName, record) => {
  const db = await openDB()
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], "readwrite")
      const store = transaction.objectStore(storeName)
      const request = store.put(record)

      request.onsuccess = () => {
        db.close()
        resolve()
      }
      request.onerror = () => {
        db.close()
        reject(request.error)
      }
    })
  } catch (error) {
    db.close()
    throw error
  }
}

/**
 * Deletes a record from a specified store by key.
 *
 * @param {string} storeName - The name of the object store
 * @param {string|Array} key - The key or compound key to delete
 * @returns {Promise<void>} A promise that resolves when the record is deleted
 */
export const deleteRecord = async (storeName, key) => {
  const db = await openDB()
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], "readwrite")
      const store = transaction.objectStore(storeName)
      const request = store.delete(key)

      request.onsuccess = () => {
        db.close()
        resolve()
      }
      request.onerror = () => {
        db.close()
        reject(request.error)
      }
    })
  } catch (error) {
    db.close()
    throw error
  }
}

/**
 * Retrieves all records from a specified store.
 *
 * @param {string} storeName - The name of the object store
 * @returns {Promise<Array>} A promise that resolves to an array of all records
 */
export const getAllRecords = async storeName => {
  const db = await openDB()
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], "readonly")
      const store = transaction.objectStore(storeName)
      const request = store.getAll()

      request.onsuccess = () => {
        db.close()
        resolve(request.result)
      }
      request.onerror = () => {
        db.close()
        reject(request.error)
      }
    })
  } catch (error) {
    db.close()
    throw error
  }
}

/**
 * Clears all records from a specified store.
 *
 * @param {string} storeName - The name of the object store
 * @returns {Promise<void>} A promise that resolves when the store is cleared
 */
export const clearStore = async storeName => {
  const db = await openDB()
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], "readwrite")
      const store = transaction.objectStore(storeName)
      const request = store.clear()

      request.onsuccess = () => {
        db.close()
        resolve()
      }
      request.onerror = () => {
        db.close()
        reject(request.error)
      }
    })
  } catch (error) {
    db.close()
    throw error
  }
}
